import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { startCheckout } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { Overline } from "@/components/site/Reveal";

export default function PayDialog({
  trigger,
  packageId,
  path = null,
  priceLabel,
  title,
  description,
  overline = "Reserve your place",
  testId = "pay-dialog",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    try {
      setLoading(true);
      toast.loading("Opening secure checkout…", { id: "pay" });
      await startCheckout(packageId, { name: form.name, email: form.email, path });
    } catch {
      toast.error("Could not start checkout. Please try again.", { id: "pay" });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-background border-border sm:max-w-lg p-8 md:p-10"
        data-testid={`${testId}-content`}
      >
        <DialogHeader className="text-left space-y-3">
          <Overline>{overline}</Overline>
          <DialogTitle className="font-serif text-3xl md:text-4xl font-light leading-tight">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-muted-foreground text-base leading-relaxed">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="mt-6 space-y-7">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Name
            </label>
            <input
              data-testid={`${testId}-name`}
              className="field-underline mt-1"
              value={form.name}
              onChange={update("name")}
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Email
            </label>
            <input
              data-testid={`${testId}-email`}
              type="email"
              className="field-underline mt-1"
              value={form.email}
              onChange={update("email")}
              placeholder="you@email.com"
            />
          </div>
          <button
            type="submit"
            data-testid={`${testId}-submit`}
            disabled={loading}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(193,119,103,0.6)] disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <>
                Continue to Payment{priceLabel ? ` · ${priceLabel}` : ""}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
